import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { JSX } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  readAssumedRole,
  readSelectedTenant,
  writeAssumedRole,
  writeSelectedTenant,
} from "../api/tenantStorage.js";
import { useMe, type MembershipDTO, type MeDTO } from "../query/me.js";
import { PRIVAT, resolveSelection } from "./activeSelection.js";

/**
 * Die Rechte, wie der Vertrag sie kennt. Aus dem OpenAPI-Vertrag abgeleitet und nicht
 * als `string`: ein Tippfehler in einer Rechteprüfung soll beim Übersetzen auffallen,
 * nicht als stillschweigend ausgeblendeter Menüpunkt.
 */
export type Permission = MembershipDTO["permissions"][number];

/**
 * Als wer handle ich gerade.
 *
 * Ein Konto kann mehreres zugleich sein: Kunde ist es immer, dazu vielleicht
 * Kontrolleur im Kino und Service-Editor in einer Praxis. Welche dieser Rollen die
 * Oberfläche gerade zeigt, ist eine bewusste Wahl — und sie bestimmt die Shell, die
 * Rollenfarbe und den Mandanten-Header jeder Abfrage.
 *
 * `null` heisst: als Privatperson. Das ist die Vorgabe für jeden, der nirgends
 * Mitglied ist, und bleibt für alle anderen erreichbar — wer im Kino arbeitet, geht
 * abends trotzdem woanders ins Kino.
 */
export interface ActiveMembershipApi {
  me: MeDTO | null;
  isPending: boolean;
  /** Alle Mitgliedschaften dieses Kontos. */
  memberships: MembershipDTO[];
  /** Die gewählte Mitgliedschaft, oder null für die Kundenansicht. */
  active: MembershipDTO | null;
  /** Mandanten-Id wählen, oder null für die Kundenansicht. */
  setActive: (tenantId: string | null) => void;
  /** Nur interne Admins: in einem Mandanten eine beliebige Rolle einnehmen. */
  assume: (tenantId: string, role: MembershipDTO["role"]) => void;
  /** Die aktive Rolle ist intern eingenommen, nicht aus einer Mitgliedschaft. */
  assumed: boolean;
  /** Rechte der aktiven Mitgliedschaft. Leer als Privatperson. */
  permissions: Permission[];
  can: (permission: Permission) => boolean;
}

const ActiveMembershipContext = createContext<ActiveMembershipApi | null>(null);

export function useActiveMembership(): ActiveMembershipApi {
  const context = useContext(ActiveMembershipContext);
  if (!context) {
    throw new Error("useActiveMembership benötigt einen ActiveMembershipProvider.");
  }
  return context;
}

export function ActiveMembershipProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}): JSX.Element {
  const me = useMe(enabled);
  const memberships = useMemo(() => me.data?.memberships ?? [], [me.data]);

  const queryClient = useQueryClient();
  const [stored, setStored] = useState<string | null>(() => readSelectedTenant());
  const [storedRole, setStoredRole] = useState<string | null>(() => readAssumedRole());

  const { active, assumed } = useMemo(
    () => resolveSelection(me.data, stored, storedRole),
    [me.data, stored, storedRole],
  );

  // Mandant und Rolle stecken im Header jeder Abfrage. Vor dem Zeichnen setzen, damit
  // keine Abfrage noch gegen die vorige Wahl hinausgeht.
  useEffect(() => {
    writeSelectedTenant(active ? active.tenantId : null);
    writeAssumedRole(active && assumed ? active.role : null);
  }, [active, assumed]);

  // Antworten der vorigen Ansicht gehören nicht in die neue: ohne das zeigte das
  // Backoffice nach einem Wechsel kurz die Buchungen des anderen Mandanten.
  const choose = useCallback(
    (tenant: string, role: string | null) => {
      setStored(tenant);
      setStoredRole(role);
      writeSelectedTenant(tenant === PRIVAT ? null : tenant);
      writeAssumedRole(role);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
    },
    [queryClient],
  );

  const setActive = useCallback(
    (tenantId: string | null) => choose(tenantId ?? PRIVAT, null),
    [choose],
  );
  const assume = useCallback(
    (tenantId: string, role: MembershipDTO["role"]) => choose(tenantId, role),
    [choose],
  );

  const permissions = useMemo(() => active?.permissions ?? [], [active]);

  const value = useMemo<ActiveMembershipApi>(
    () => ({
      me: me.data ?? null,
      isPending: enabled && me.isPending,
      memberships,
      active,
      setActive,
      assume,
      assumed,
      permissions,
      can: (permission: Permission) => permissions.includes(permission),
    }),
    [me.data, me.isPending, enabled, memberships, active, setActive, assume, assumed, permissions],
  );

  return (
    <ActiveMembershipContext.Provider value={value}>{children}</ActiveMembershipContext.Provider>
  );
}
